import * as React from 'react';
import { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    H5Emphasized,
    isWeb,
    RadioInputWithLabel,
    redactPrivate,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import {
    type EmploymentType,
    type IndustryType,
    READABLE_EMPLOYMENT_TYPE_MAP,
    READABLE_INDUSTRY_TYPE_MAP,
} from '../../../types/Edd';
import KycButton from './KycButton';

interface EmploymentProps {
    setKycData: (data: Partial<RunKYCVerifyBody>) => void;
    kycData: Partial<RunKYCVerifyBody>;
}

const Employment = ({ setKycData, kycData }: EmploymentProps) => {
    const [employmentType, setEmploymentType] = useState<EmploymentType | null>(
        kycData.employmentType ?? null,
    );
    const [occupationIndustry, setOccupationIndustry] =
        useState<IndustryType | null>(kycData.occupationIndustry ?? null);

    const updateKycData = React.useCallback(() => {
        if (employmentType != null && occupationIndustry != null) {
            setKycData({
                employmentType,
                occupationIndustry,
            });
        }
    }, [employmentType, occupationIndustry]);

    React.useEffect(() => {
        updateKycData();
    }, [updateKycData]);

    return (
        <View paddingBottom={24}>
            <H5Emphasized marginBottom={8}>Employment</H5Emphasized>
            <BodyMediumRegular color="$gray1000" marginBottom={24}>
                We&apos;re required to ask you some questions about your
                employment.
            </BodyMediumRegular>
            <BodyLargeEmphasized marginBottom={12}>
                What&apos;s your employment status?
            </BodyLargeEmphasized>
            <YStack gap={6} marginRight={12} {...redactPrivate()}>
                {Object.keys(READABLE_EMPLOYMENT_TYPE_MAP).map((value) => (
                    <RadioInputWithLabel
                        key={value}
                        label={value}
                        isSelected={
                            employmentType ===
                            READABLE_EMPLOYMENT_TYPE_MAP[value]
                        }
                        onPress={() => {
                            setEmploymentType(
                                READABLE_EMPLOYMENT_TYPE_MAP[value] ?? null,
                            );
                        }}
                    />
                ))}
            </YStack>
            <BodyLargeEmphasized marginTop={24} marginBottom={12}>
                What&apos;s your occupation?
            </BodyLargeEmphasized>
            <YStack gap={6} paddingRight={20} {...redactPrivate()}>
                {Object.keys(READABLE_INDUSTRY_TYPE_MAP).map((value) => (
                    <RadioInputWithLabel
                        key={value}
                        label={value}
                        isSelected={
                            occupationIndustry ===
                            READABLE_INDUSTRY_TYPE_MAP[value]
                        }
                        onPress={() => {
                            setOccupationIndustry(
                                READABLE_INDUSTRY_TYPE_MAP[value] ?? null,
                            );
                        }}
                    />
                ))}
            </YStack>
        </View>
    );
};

interface EmploymentStepProps extends EmploymentProps {
    onPressNext: () => void;
}

export const EmploymentStep = ({
    setKycData,
    kycData,
    onPressNext,
}: EmploymentStepProps) => {
    return (
        <YStack
            flex={1}
            justifyContent={isWeb ? 'flex-start' : 'space-between'}
            gap={16}
        >
            <ScrollView
                $mobile_mini={{ height: 400 }}
                maxHeight={isWeb ? '70vh' : '100%'} // height adjustment needed for mobile web when in sheet
            >
                <Employment kycData={kycData} setKycData={setKycData} />
                <KycButton
                    onPress={() => {
                        onPressNext(); // Must call the handler this way to prevent sending along the onPress event as form data
                    }}
                    disabled={
                        kycData.employmentType == null ||
                        kycData.occupationIndustry == null
                    }
                />
            </ScrollView>
        </YStack>
    );
};

export default Employment;
